import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { HTTPStatusCode } from 'config/const';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { ApiQueryOperator, IAction, IApiList, IAuthMFARequired, IPaymentSetCalculations, IPayout, IReviewPayout, IUserPayoutCalculations } from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

export function* reviewPayout({ payload }: IAction<ActionTypes.Payout.REVIEW_PAYOUT_REQUEST, { payoutId: string; body: IReviewPayout; onMFARequired(request: IAuthMFARequired): void; onSuccess?(): void; onFail?(): void }>) {
  const { payoutId, body, onMFARequired, onSuccess, onFail } = payload;
  try {
    const response: ApiResponse<IPayout | IAuthMFARequired> = yield call(API.Payments.reviewPayout, payoutId, body);

    if (response.statusCode.toString() === HTTPStatusCode.PARTIAL_CONTENT) {
      const stepUpResponse = response.body as IAuthMFARequired;
      yield call(onMFARequired, stepUpResponse);
    } else {
      if (onSuccess) yield call(onSuccess);
      yield put(Actions.reviewPayoutSuccess(response.body as IPayout));
    }
  } catch (error: any) {
    if (onFail) yield call(onFail);
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.reviewPayoutError(formattedError));
  }
}

export function* getUserPayout({ payload }: IAction<ActionTypes.Payout.GET_USER_PAYOUT_REQUEST, { payoutId: string; onSuccess: (payout: IPayout) => void }>) {
  try {
    const { payoutId, onSuccess } = payload;

    const options = {
      limit: 0,
      q: [{ key: 'id', value: payoutId, operator: ApiQueryOperator.eq }],
    };
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Users.listPayouts, options);

    const resBody = response?.body?.data;
    if (!resBody?.length) {
      yield call(browserHistory.push, routeConstants.private.notFound);
    } else {
      yield call(onSuccess, resBody[0]);
      yield put(Actions.getUserPayoutSuccess(resBody[0]));
    }
  } catch (error: any) {
    yield put(Actions.getUserPayoutError(error));
  }
}

export function* getUserPayoutCalculations({ payload }: IAction<ActionTypes.Payout.GET_USER_PAYOUT_CALCULATIONS_REQUEST, { payoutId: string; onSuccess: (response: IUserPayoutCalculations) => void }>) {
  try {
    const { payoutId, onSuccess } = payload;

    const response: ApiResponse<IPaymentSetCalculations> = yield call(API.Users.showPayoutTipCalculations, payoutId);

    yield call(onSuccess, { ...response.body, payoutId });
    yield put(Actions.getUserPayoutCalculationsSuccess({ ...response.body, payoutId }));
  } catch (error: any) {
    yield put(Actions.getUserPayoutCalculationsError(error));
  }
}

export function* watchReviewPayout() {
  yield takeEvery(ActionTypes.Payout.REVIEW_PAYOUT_REQUEST, reviewPayout);
}

export function* watchGetUserPayout() {
  yield takeEvery(ActionTypes.Payout.GET_USER_PAYOUT_REQUEST, getUserPayout);
}

export function* watchGetUserPayoutCalculations() {
  yield takeEvery(ActionTypes.Payout.GET_USER_PAYOUT_CALCULATIONS_REQUEST, getUserPayoutCalculations);
}

function* payoutSaga() {
  yield all([fork(watchReviewPayout), fork(watchGetUserPayout), fork(watchGetUserPayoutCalculations)]);
}
export { payoutSaga };
